import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarDays, Download, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";
import { downloadCsv } from "@/lib/exportCsv";

const todayISO = () => new Date().toISOString().split("T")[0];

export default function EodSnapshotsPage() {
  const [reportDate, setReportDate] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadReport = useCallback(async (dateValue) => {
    setLoading(true);
    try {
      const [snapshotRes, historyRes] = await Promise.all([
        axios.get(`${API}/reports/eod-snapshot`, { params: { snapshot_date: dateValue }, withCredentials: true }),
        axios.get(`${API}/reports/eod-snapshots`, { withCredentials: true }),
      ]);
      setReport(snapshotRes.data);
      setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
    } catch (error) {
      console.error("EOD load failed", error);
      toast.error("Failed to load End of Day report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport(reportDate);
  }, [loadReport, reportDate]);

  const storeSnapshot = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/reports/eod-snapshot`,
        {},
        { params: { report_date: reportDate }, withCredentials: true }
      );
      setReport({ ...data, source: "snapshot" });
      toast.success(`Snapshot stored for ${reportDate}`);
      loadReport(reportDate);
    } catch (error) {
      console.error("Snapshot save failed", error);
      toast.error("Failed to store snapshot");
    } finally {
      setSaving(false);
    }
  };

  const exportReport = () => {
    if (!report) return;
    const reps = Object.keys(report.total_visits_per_rep || {});
    const rows = reps.length
      ? reps.map((rep) => [
          report.snapshot_date,
          report.total_referrals_today,
          report.new_doctors_today,
          report.current_month_referrals_to_date,
          report.forecasted_month_referrals,
          rep,
          report.total_visits_per_rep[rep],
          report.first_input_time_per_rep?.[rep] || "",
          report.last_input_time_per_rep?.[rep] || "",
        ])
      : [[
          report.snapshot_date,
          report.total_referrals_today,
          report.new_doctors_today,
          report.current_month_referrals_to_date,
          report.forecasted_month_referrals,
          "No visits captured",
          0,
          "",
          "",
        ]];

    downloadCsv(
      `end-of-day-${report.snapshot_date}`,
      [
        "Report date",
        "Referrals today",
        "New doctors today",
        "Month to date",
        "Forecast month",
        "Rep",
        "Visits",
        "First input",
        "Last input",
      ],
      rows
    );
  };

  const repRows = Object.entries(report?.total_visits_per_rep || {});

  return (
    <div className="min-h-screen bg-gray-50" data-testid="eod-snapshots-page">
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-2xl font-bold tracking-tight">End of Day Snapshots</h1>
        <p className="text-sm text-muted-foreground">Daily referral and rep activity history, stored per day</p>
      </div>

      <div className="px-6 pb-6 flex flex-wrap items-end gap-3 bg-white border-y py-4">
        <div className="w-full sm:w-56">
          <Label className="text-xs">Report date</Label>
          <Input
            type="date"
            value={reportDate}
            max={todayISO()}
            onChange={(e) => setReportDate(e.target.value)}
            className="mt-1"
            data-testid="eod-date-input"
          />
        </div>
        <Button onClick={storeSnapshot} disabled={saving} className="gap-2" data-testid="eod-store-btn">
          <Save className="h-4 w-4" />
          {saving ? "Storing..." : "Store snapshot"}
        </Button>
        <Button variant="outline" onClick={exportReport} disabled={!report} className="gap-2" data-testid="eod-export-btn">
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
        {report && (
          <Badge variant={report.source === "snapshot" ? "default" : "secondary"} data-testid="eod-source-badge">
            {report.source === "snapshot" ? "Stored snapshot" : "Live calculation"}
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24" data-testid="eod-loading">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        report && (
          <div className="px-6 py-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { label: "Referrals today", value: report.total_referrals_today, testId: "eod-kpi-referrals" },
                { label: "New doctors", value: report.new_doctors_today, testId: "eod-kpi-new-doctors" },
                { label: "Month to date", value: report.current_month_referrals_to_date, testId: "eod-kpi-mtd" },
                { label: "Forecast month", value: report.forecasted_month_referrals, testId: "eod-kpi-forecast" },
              ].map((item) => (
                <Card key={item.label} data-testid={item.testId}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">{item.label}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{item.value}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card data-testid="eod-rep-activity">
              <CardHeader>
                <CardTitle className="text-base">Rep activity</CardTitle>
                <CardDescription>
                  Visits captured on {report.snapshot_date} · {report.working_days_elapsed} of {report.working_days_in_month} working
                  days elapsed
                </CardDescription>
              </CardHeader>
              <CardContent>
                {repRows.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No visits were captured on this date.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rep</TableHead>
                        <TableHead className="text-center">Visits</TableHead>
                        <TableHead className="text-center">First input</TableHead>
                        <TableHead className="text-center">Last input</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {repRows.map(([rep, visits]) => (
                        <TableRow key={rep} data-testid={`eod-rep-row-${rep.replace(/\s+/g, "-").toLowerCase()}`}>
                          <TableCell className="font-medium">{rep}</TableCell>
                          <TableCell className="text-center">{visits}</TableCell>
                          <TableCell className="text-center">{report.first_input_time_per_rep?.[rep] || "—"}</TableCell>
                          <TableCell className="text-center">{report.last_input_time_per_rep?.[rep] || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card data-testid="eod-history-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  Stored history
                </CardTitle>
                <CardDescription>Snapshots already saved — click a date to open it</CardDescription>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No snapshots stored yet. Use "Store snapshot" to keep today's figures.</p>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {history.map((item) => (
                      <button
                        key={item.snapshot_date}
                        onClick={() => setReportDate(item.snapshot_date)}
                        className="w-full flex items-center justify-between text-left bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg px-3 py-2"
                        data-testid={`eod-history-${item.snapshot_date}`}
                      >
                        <span className="text-sm font-medium">{item.snapshot_date}</span>
                        <span className="text-xs text-muted-foreground">
                          {item.total_referrals_today} referrals · forecast {item.forecasted_month_referrals}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )
      )}
    </div>
  );
}
